import {FC, ReactNode} from 'react'

import Carousel from '@/components/common/carousel/Carousel'
import {Link} from '@/i18n'
import {SLIDER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Button} from '../content/Button'
import {SectionLayout} from '../content/SectionLayout'
import {Text, TextVariant} from '../content/Text'
import {ArrowIcon} from '../icons/ArrowIcon'
import {CardListLayout} from './CardListLayout'

const carouselSettings = {
  infinite: true,
  speed: SLIDER_TRANSITION,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  dots: true,
  autoplay: true,
  autoplaySpeed: 8000,
}

type PricingOfferCardsSectionFooter = {
  title?: string
  description?: string
  button?: {text: string; linkHref: string; onClick?: () => void}
}

type PricingOfferCardsSectionProps = {
  title: string
  description?: string
  cards: ReactNode
  footer: PricingOfferCardsSectionFooter
}

export const PricingOfferCardsSection: FC<PricingOfferCardsSectionProps> = ({
  title,
  description,
  cards,
  footer,
}) => (
  <>
    <SectionLayout
      title={{text: title}}
      description={description ? {text: description} : undefined}
    >
      <div className="cards-wrapper">
        <div className="desktop-wrapper">
          <CardListLayout>{cards}</CardListLayout>
        </div>
        <div className="mobile-wrapper">
          <div className="card-carousel">
            <Carousel settings={carouselSettings}>{cards}</Carousel>
          </div>
        </div>
      </div>
      {footer && (
        <div className="footer">
          {(footer.title || footer.description) && (
            <div className="footer-content">
              {footer.title && (
                <Text variant={TextVariant.h5} element="h3" align="center">
                  {footer.title}
                </Text>
              )}
              {footer.description && <Text align="center">{footer.description}</Text>}
            </div>
          )}
          {footer.button && (
            <Link href={footer.button.linkHref} onClick={footer.button.onClick}>
              <Button
                type="button"
                icon={<ArrowIcon color={theme.palette.primary.main} />}
                isIconAnimated
                isHoverable
              >
                {footer.button.text}
              </Button>
            </Link>
          )}
        </div>
      )}
    </SectionLayout>

    <style jsx>{`
      .mobile-wrapper {
        display: none;
      }
      .footer {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        margin-top: ${theme.spacing[7]};
        gap: ${theme.spacing[3.5]};
      }
      .footer-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${theme.spacing[1]};
      }
      @media ${MOBILE_SCREENSIZE} {
        .desktop-wrapper {
          display: none;
        }
        .mobile-wrapper {
          display: block;
        }
        .cards-wrapper {
          margin-top: ${theme.spacing[5.5]};
        }
        .footer {
          margin-top: ${theme.spacing[6]};
        }
      }
    `}</style>

    {/* Please, use global styles only if you need to override CSS from external libraries */}
    <style jsx global>{`
      .card-carousel .slick-list {
        margin: 0 -${theme.spacing[2]};
      }
      .card-carousel .slick-slide {
        padding: 0 ${theme.spacing[2]};
      }
      .card-carousel .slick-track {
        display: flex !important;
        align-items: stretch;
      }
      .card-carousel .slick-slide {
        height: auto !important;
      }
      .card-carousel .slick-slide > div {
        height: 100%;
      }
    `}</style>
  </>
)
